import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { getBrandProfile, putBrandProfile, listAssets, uploadAsset, createData, updateClient, getClient } from "@/lib/api";
import { useClient } from "@/context/ClientContext";
import { PageHeader, PageContainer } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Flame, Plus, FloppyDisk, Package, UploadSimple, CheckCircle, Gauge } from "@phosphor-icons/react";
import { toast } from "sonner";

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const FIELDS = [
  { k: "name", l: "Brand name" }, { k: "industry", l: "Industry" },
  { k: "website", l: "Website" }, { k: "accent_color", l: "Primary color (hex)" },
  { k: "secondary_color", l: "Secondary color (hex)" }, { k: "fonts", l: "Fonts" },
  { k: "domains", l: "Domains" }, { k: "social_accounts", l: "Social accounts" },
];

export default function BrandManagerPage() {
  const { activeId, activeClient, clients, setActive, refresh } = useClient();
  const qc = useQueryClient();
  const profileQ = useQuery({ queryKey: ["brand-profile", activeId], queryFn: () => getBrandProfile(activeId), enabled: !!activeId });
  const clientQ = useQuery({ queryKey: ["client-doc", activeId], queryFn: () => getClient(activeId), enabled: !!activeId });
  const [form, setForm] = useState(null);
  const f = form || profileQ.data || {};
  React.useEffect(() => { if (profileQ.data) setForm(profileQ.data); }, [profileQ.data]);

  const brandQ = useQuery({ queryKey: ["assets", "brand", activeId], queryFn: () => listAssets(activeId, "brand"), enabled: !!activeId });

  const save = useMutation({
    mutationFn: () => putBrandProfile({ client_id: activeId, ...FIELDS.reduce((a, x) => ({ ...a, [x.k]: f[x.k] || "" }), {}), brand_voice: f.brand_voice || "", description: f.description || "", products_note: f.products_note || "" }),
    onSuccess: () => { toast.success("Brand saved"); refresh(); qc.invalidateQueries({ queryKey: ["brand-profile", activeId] }); },
  });
  const uploadBrand = useMutation({
    mutationFn: ({ file, kind }) => uploadAsset(activeId, file, "brand", kind),
    onSuccess: () => { toast.success("Asset saved"); qc.invalidateQueries({ queryKey: ["assets", "brand", activeId] }); },
  });

  const newBrand = async () => {
    const name = window.prompt("New brand name"); if (!name) return;
    const c = await createData("clients", { name, accent_color: "#6B3DFF" });
    await refresh(); setActive(c.id); toast.success(`Created ${name}`);
  };

  return (
    <PageContainer>
      <PageHeader eyebrow="Brand Manager" title="Brands" subtitle="Unlimited brands. Switching brand instantly updates the entire AI Studio — assets, voice, colors and products." />

      <div className="flex items-center gap-2 flex-wrap mb-5" data-testid="brand-switcher">
        {clients.map((c) => (
          <button key={c.id} data-testid={`brand-chip-${c.id}`} onClick={() => setActive(c.id)}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm ${c.id === activeId ? "bg-[#6B3DFF] text-white border-[#6B3DFF]" : "border-white/10 text-[#cfcfcf] hover:border-white/25"}`}>
            <Flame size={15} weight={c.id === activeId ? "fill" : "regular"} /> {c.name}
          </button>
        ))}
        <Button data-testid="brand-new-btn" size="sm" variant="outline" onClick={newBrand} className="border-white/15 bg-transparent text-white hover:bg-white/5"><Plus size={15} className="mr-1" /> New brand</Button>
      </div>

      {/* Automation settings */}
      <div data-testid="automation-settings" className="card-matte rounded-xl px-4 py-3.5 mb-5 flex flex-wrap items-center gap-4">
        <div className="flex items-center gap-2 text-sm font-semibold text-white"><Gauge size={16} className="brand-text" weight="fill" /> Automation</div>
        <div className="flex items-center gap-3">
          <Switch data-testid="auto-score-toggle" checked={!!clientQ.data?.auto_score}
            onCheckedChange={async (v) => {
              await updateClient(activeId, { auto_score: v });
              qc.invalidateQueries({ queryKey: ["client-doc", activeId] });
              toast.success(v ? "Auto-Scoring ON — every generated asset gets a Marketing Score + campaign report automatically"
                : "Auto-Scoring OFF — score assets on demand from the AI Assistant menu");
            }} />
          <div>
            <div className="text-sm text-white">Auto Score Every Asset</div>
            <div className="text-[11px] text-[#8a8a8a]">When on, new campaign assets are scored automatically and a Campaign Intelligence report is generated. Turn off to score on demand and reduce AI usage.</div>
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 card-matte p-5 space-y-3" data-testid="brand-profile-form">
          <div className="font-head font-semibold">{activeClient?.name} · Profile</div>
          <div className="grid sm:grid-cols-2 gap-3">
            {FIELDS.map((x) => (
              <div key={x.k}><Label className="label-caps">{x.l}</Label>
                <Input data-testid={`brand-${x.k}`} value={f[x.k] || ""} onChange={(e) => setForm({ ...f, [x.k]: e.target.value })} className="bg-[#10101A] border-white/10 mt-1" /></div>
            ))}
          </div>
          <div><Label className="label-caps">Brand voice</Label>
            <Textarea data-testid="brand-voice" value={f.brand_voice || ""} onChange={(e) => setForm({ ...f, brand_voice: e.target.value })} rows={3} className="bg-[#10101A] border-white/10 mt-1" /></div>
          <div><Label className="label-caps">Products / catalog notes</Label>
            <Textarea data-testid="brand-products" value={f.products_note || ""} onChange={(e) => setForm({ ...f, products_note: e.target.value })} rows={2} className="bg-[#10101A] border-white/10 mt-1" /></div>
          <div><Label className="label-caps">Email templates / notes</Label>
            <Textarea data-testid="brand-email" value={f.email_templates || ""} onChange={(e) => setForm({ ...f, email_templates: e.target.value })} rows={2} className="bg-[#10101A] border-white/10 mt-1" /></div>
          <Button data-testid="brand-save-btn" onClick={() => save.mutate()} disabled={save.isPending} className="bg-[#6B3DFF] hover:bg-[#A56CFF] text-white"><FloppyDisk size={16} className="mr-1.5" /> {save.isPending ? "Saving…" : "Save brand"}</Button>
        </div>

        <div className="card-matte p-5" data-testid="brand-assets-mini">
          <div className="flex items-center gap-2 mb-1"><Package size={16} className="brand-text" weight="fill" /><span className="font-head font-semibold text-sm">Brand Assets</span></div>
          <p className="text-xs text-[#7a7a7a] mb-3">Applied automatically across the AI Studio.</p>
          <div className="grid grid-cols-3 gap-2 mb-3">
            {(brandQ.data || []).filter((a) => a.is_active).map((a) => (
              <div key={a.id} className="rounded-lg border border-emerald-500/25 bg-emerald-500/10 p-1.5 text-center">
                <img src={`${BACKEND}${a.url}`} alt={a.kind} className="h-12 w-full object-contain rounded bg-[#0a0a0a]" />
                <div className="text-[10px] text-[#cfcfcf] capitalize mt-1 flex items-center justify-center gap-1"><CheckCircle size={10} className="text-emerald-400" weight="fill" /> {a.kind}</div>
              </div>
            ))}
            {!(brandQ.data || []).some((a) => a.is_active) && <div className="col-span-3 text-xs text-[#7a7a7a]">No active assets.</div>}
          </div>
          <div className="grid grid-cols-2 gap-2">
            {["logo", "packaging", "mug", "label"].map((k) => (
              <label key={k} data-testid={`brand-upload-${k}`} className="cursor-pointer text-xs text-center px-2 py-2 rounded-lg border border-white/10 text-[#9a9a9a] hover:text-white hover:border-white/25 capitalize">
                <UploadSimple size={13} className="inline mr-1" /> {k}
                <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { const file = e.target.files?.[0]; if (file) uploadBrand.mutate({ file, kind: k }); e.target.value = ""; }} />
              </label>
            ))}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}
